import { z } from "zod";
import { Id } from "../common/ids.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
/**
 * Every notification type with its parameters and default channels (NOT-1). In-app is always on; email and SMS
 * follow the user's preferences, starting from these defaults. Clients render in-app text from
 * `notifications.<type>` in their resource files with the same parameters. A `reason` the law forbids saying comes
 * empty, with `reasonWithheld` set to "true", and reads as the generic reason (card 126).
 */
export const NOTIFICATION_CATALOGUE = {
    "enterprise.verified": { params: ["enterpriseName"], email: true, sms: false },
    "enterprise.rejected": { params: ["enterpriseName", "reason"], email: true, sms: false },
    "enterprise.info_requested": { params: ["enterpriseName", "reason"], email: true, sms: false },
    "enterprise.suspended": { params: ["enterpriseName", "reason"], email: true, sms: false },
    "enterprise.reinstated": { params: ["enterpriseName", "reason"], email: true, sms: false },
    "enterprise.duplicate_registration": {
        params: ["enterpriseName", "registrationNumber", "country"],
        email: true,
        sms: false,
    },
    "contact.request_received": { params: ["personName", "enterpriseName"], email: true, sms: false },
    "contact.request_accepted": { params: ["personName", "enterpriseName"], email: false, sms: false },
    "contact.transferred": {
        params: ["personName", "successorName", "enterpriseName"],
        email: true,
        sms: false,
    },
    "conversation.message_removed": { params: ["personName", "reason"], email: true, sms: false },
    "conversation.file_removed": { params: ["personName", "fileName", "reason"], email: true, sms: false },
    "product.approved": { params: ["productName", "reason"], email: true, sms: false },
    "product.rejected": { params: ["productName", "reason"], email: true, sms: false },
    "product.revision_approved": { params: ["productName", "reason"], email: true, sms: false },
    "product.revision_rejected": { params: ["productName", "reason"], email: true, sms: false },
    "product.blocked": { params: ["productName", "reason"], email: true, sms: false },
    "product.unblocked": { params: ["productName", "reason"], email: true, sms: false },
    "inquiry.received": { params: ["enterpriseName", "productName"], email: true, sms: false },
    "inquiry.declined": { params: ["enterpriseName", "reason"], email: true, sms: false },
    "inquiry.withdrawn": { params: ["enterpriseName"], email: false, sms: false },
    "inquiry.expired": { params: ["enterpriseName"], email: true, sms: false },
    "inquiry.closed": { params: ["enterpriseName", "productName"], email: true, sms: false },
    "quote.issued": { params: ["enterpriseName"], email: true, sms: false },
    "quote.countered": { params: ["enterpriseName"], email: true, sms: false },
    "quote.accepted": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.cancelled": { params: ["enterpriseName", "orderNumber", "reason"], email: true, sms: false },
    "order.cancellation_requested": {
        params: ["enterpriseName", "orderNumber", "reason"],
        email: true,
        sms: false,
    },
    "order.cancellation_declined": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.payment_due": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.payment_reminder": { params: ["enterpriseName", "orderNumber"], email: true, sms: true },
    "order.payment_overdue": { params: ["orderNumber"], email: true, sms: false },
    "order.payment_window_lapsed": { params: ["orderNumber"], email: true, sms: false },
    "order.transfer_reported": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.transfer_confirmed": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.transfer_rejected": { params: ["enterpriseName", "orderNumber", "reason"], email: true, sms: false },
    "shop.bank_account_changed": { params: ["accountEnding"], email: true, sms: false },
    "order.shipped": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.delivered": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.shipment_exception": { params: ["orderNumber", "trackingNumber"], email: true, sms: false },
    "order.confirmation_reminder": { params: ["enterpriseName", "orderNumber"], email: true, sms: true },
    "order.receipt_confirmed": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.auto_confirmed": { params: ["orderNumber"], email: true, sms: false },
    "order.confirmation_paused": { params: ["orderNumber"], email: true, sms: false },
    "order.change_proposed": { params: ["enterpriseName", "orderNumber", "reason"], email: true, sms: false },
    "order.change_accepted": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.change_declined": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "order.change_withdrawn": { params: ["enterpriseName", "orderNumber"], email: false, sms: false },
    "aftersale.opened": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "aftersale.withdrawn": { params: ["enterpriseName", "orderNumber"], email: false, sms: false },
    "aftersale.accepted": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "aftersale.countered": { params: ["enterpriseName", "orderNumber", "reason"], email: true, sms: false },
    "aftersale.rejected": { params: ["enterpriseName", "orderNumber", "reason"], email: true, sms: false },
    "aftersale.counter_accepted": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "aftersale.escalated": { params: ["orderNumber"], email: true, sms: false },
    "aftersale.lapsed": { params: ["orderNumber"], email: true, sms: false },
    "aftersale.return_shipped": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "aftersale.return_received": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "aftersale.settlement_recorded": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "aftersale.settled": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "aftersale.settlement_not_received": {
        params: ["enterpriseName", "orderNumber", "reason"],
        email: true,
        sms: false,
    },
    "aftersale.auto_settled": { params: ["orderNumber"], email: true, sms: false },
    "review.received": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "review.replied": { params: ["enterpriseName", "orderNumber"], email: false, sms: false },
    "review.hidden": { params: ["orderNumber", "reason"], email: true, sms: false },
    "invoice.issued": { params: ["enterpriseName", "orderNumber", "number"], email: true, sms: false },
    "invoice.credit_note_issued": {
        params: ["enterpriseName", "orderNumber", "number", "invoiceNumber"],
        email: true,
        sms: false,
    },
    "aftersale.decided": { params: ["orderNumber", "reason"], email: true, sms: false },
    "aftersale.appealed": { params: ["enterpriseName", "orderNumber"], email: true, sms: false },
    "aftersale.decision_final": { params: ["orderNumber"], email: true, sms: false },
};
export const NOTIFICATION_TYPES = Object.keys(NOTIFICATION_CATALOGUE);
export const NotificationTypeSchema = z.enum(NOTIFICATION_TYPES).meta({ id: "NotificationType" });
/**
 * Notices the platform terms (17.1) give the enterprise's admins by email as well as in-app: verification decisions,
 * suspension and reinstatement, a second registration of the number, and a change of the shop's bank account. Their
 * email cannot be turned off (card 128).
 */
export const EMAIL_REQUIRED_NOTIFICATIONS = [
    "enterprise.verified",
    "enterprise.rejected",
    "enterprise.info_requested",
    "enterprise.suspended",
    "enterprise.reinstated",
    "enterprise.duplicate_registration",
    "shop.bank_account_changed",
];
export const Notification = z
    .object({
    id: Id,
    type: NotificationTypeSchema,
    params: z.record(z.string(), z.string()),
    link: z.string().describe("Path in the workspace, without the locale, where the action is taken"),
    enterpriseId: Id.nullable(),
    readAt: Timestamp.nullable(),
    createdAt: Timestamp,
})
    .meta({ id: "Notification" });
export const listNotifications = defineRoute({
    id: "notifications.list",
    method: "GET",
    path: "/v1/notifications",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "The signed-in user's in-app notifications from every enterprise, newest first, with the unread count",
    prd: ["NOT-1"],
    request: {
        query: z.object({
            limit: z.coerce.number().int().min(1).max(50).default(20),
            cursor: z.string().min(1).optional(),
            unread: z.enum(["true", "false"]).optional(),
        }),
    },
    responses: {
        200: {
            description: "A page of notifications",
            schema: z.object({
                items: z.array(Notification),
                nextCursor: z.string().nullable(),
                unreadCount: z.number().int().nonnegative(),
            }),
        },
    },
    errors: ["AUTH_REQUIRED", "VALIDATION_FAILED"],
});
export const readNotification = defineRoute({
    id: "notifications.read",
    method: "POST",
    path: "/v1/notifications/{notificationId}/read",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Mark one notification read; marking it again is harmless",
    prd: ["NOT-1"],
    request: { params: z.object({ notificationId: Id }) },
    responses: { 204: { description: "Read" } },
    errors: ["AUTH_REQUIRED", "NOT_FOUND"],
});
export const readAllNotifications = defineRoute({
    id: "notifications.read-all",
    method: "POST",
    path: "/v1/notifications/read-all",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Mark every notification read",
    prd: ["NOT-1"],
    responses: { 204: { description: "All read" } },
    errors: ["AUTH_REQUIRED"],
});
export const NotificationPreference = z
    .object({ type: NotificationTypeSchema, email: z.boolean(), sms: z.boolean() })
    .meta({ id: "NotificationPreference" });
export const getNotificationPreferences = defineRoute({
    id: "notifications.preferences.get",
    method: "GET",
    path: "/v1/me/notification-preferences",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Email and SMS per notification type, defaults filled in; in-app is always on, and so is email for the notices the terms send by email",
    prd: ["NOT-1"],
    responses: {
        200: { description: "Preferences", schema: z.object({ items: z.array(NotificationPreference) }) },
    },
    errors: ["AUTH_REQUIRED"],
});
export const putNotificationPreferences = defineRoute({
    id: "notifications.preferences.put",
    method: "PUT",
    path: "/v1/me/notification-preferences",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Set email and SMS for the listed types; types left out keep their setting. Email stays on for the notices the terms send by email (`EMAIL_REQUIRED_NOTIFICATIONS`); turning it off is refused",
    prd: ["NOT-1"],
    request: { body: z.object({ items: z.array(NotificationPreference).min(1) }) },
    responses: {
        200: { description: "Preferences", schema: z.object({ items: z.array(NotificationPreference) }) },
    },
    errors: ["AUTH_REQUIRED", "VALIDATION_FAILED"],
});
export const notificationRoutes = [
    listNotifications,
    readNotification,
    readAllNotifications,
    getNotificationPreferences,
    putNotificationPreferences,
];
//# sourceMappingURL=notifications.routes.js.map